import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
  translateFormCompletionName,
} from '@assets/i18n/config';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { DroppableFileInput } from '@ovComponents/2-component/droppableFileInput/droppableFileInput';
import { PdfViewer } from '@ovComponents/2-component/pdfViewer/pdfViewer';
import { safeWindowOpen } from '@utils/urlSanitization';
import { Box, Button, Skeleton, Typography, useSnackbar } from 'hephaestus/web';
import { Create, Download, Upload } from 'hephaestus/webIcons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const FETCH_FORM_COMPLETION = gql`
  query fetchFormCompletion($formCompletionId: ObjectID!) {
    fetchFormCompletion(formCompletionId: $formCompletionId) {
      id
      state
      templateType
      signingType
      unsignedUrl
      signedUrls
      signedDocuments {
        id
        downloadUrl
      }
      unsignedDocument {
        id
        downloadUrl
      }
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      resolvedPdfFileName { ${TRANSLATED_STRING_FIELDS} }
      translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    }
  }
`;

export const ViewFormDialog = ({
  formCompletionId,
  onClose,
  sign,
  file,
  setFile,
  signLoading,
}: {
  formCompletionId: any;
  onClose: () => void;
  sign?: () => void;
  file?: File;
  setFile: (file: File) => void;
  signLoading: boolean;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { enqueueSnackbar } = useSnackbar();
  const [formCompletion, setFormCompletion] = useState<any>(null);

  const { loading } = useQuery(FETCH_FORM_COMPLETION, {
    variables: { formCompletionId },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setFormCompletion(data.fetchFormCompletion);
    },
  });

  // TODO: Remove signedUrls once we have transitioned fully to signedDocuments
  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle onClose={onClose}>
        {loading ? (
          <Skeleton width={200} />
        ) : (
          translateFormCompletionName(formCompletion)
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Skeleton height={500} width='100%' />
        ) : (
          <Box>
            <Typography variant='bodyLarge'>
              {translateBackend(formCompletion.translatedDescription)}
            </Typography>
            {(formCompletion.signedDocuments[0]?.downloadUrl ||
              formCompletion.signedUrls[0] ||
              formCompletion.unsignedUrl ||
              formCompletion.unsignedDocument?.downloadUrl) && (
              <PdfViewer
                pdfUrl={
                  formCompletion.signedDocuments[0]?.downloadUrl ||
                  formCompletion.signedUrls[0] ||
                  formCompletion.unsignedUrl ||
                  formCompletion.unsignedDocument?.downloadUrl
                }
                fullWidth
              />
            )}
            {formCompletion.signingType === 'UPLOAD' &&
              formCompletion.state === 'UNSIGNED' && (
                <>
                  {formCompletion.templateType === 'PDF' && (
                    <Typography variant='bodyLarge'>
                      {t('workflowCompletions:uploadSignedForm')}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      mt: 2,
                    }}
                  >
                    <DroppableFileInput
                      onFileChosen={(dropFile: File) => {
                        setFile(dropFile);
                      }}
                    />
                  </Box>
                </>
              )}
          </Box>
        )}
      </DialogContent>
      {!loading && (
        <DialogFooter>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box>
              {formCompletion.templateType === 'PDF' &&
                formCompletion.unsignedUrl &&
                formCompletion.state === 'UNSIGNED' && (
                  <Button
                    onClick={() =>
                      safeWindowOpen(formCompletion.unsignedUrl, {
                        target: '_blank',
                        onError: (errorMsg) => {
                          enqueueSnackbar(errorMsg, { variant: 'error' });
                        },
                      })
                    }
                    variant='contained'
                    startIcon={<Download />}
                    color='secondary'
                  >
                    {t('shared:download')}
                  </Button>
                )}
            </Box>
            {formCompletion.state === 'UNSIGNED' &&
              sign &&
              (formCompletion.signingType === 'UPLOAD' ? (
                <Button
                  onClick={sign}
                  startIcon={<Upload />}
                  disabled={!file || signLoading}
                >
                  {t('shared:upload')}
                </Button>
              ) : (
                <Button
                  onClick={sign}
                  startIcon={<Create />}
                  disabled={signLoading}
                >
                  {t('shared:sign')}
                </Button>
              ))}
            {formCompletion.state === 'SIGNED' &&
              (formCompletion.signedDocuments[0]?.downloadUrl ||
                formCompletion.signedUrls[0]) && (
                <Button
                  onClick={() =>
                    safeWindowOpen(
                      formCompletion.signedDocuments[0]?.downloadUrl ||
                        formCompletion.signedUrls[0],
                      {
                        target: '_blank',
                        onError: (errorMsg) => {
                          enqueueSnackbar(errorMsg, { variant: 'error' });
                        },
                      },
                    )
                  }
                  startIcon={<Download />}
                >
                  {t('shared:download')}
                </Button>
              )}
          </Box>
        </DialogFooter>
      )}
    </Dialog>
  );
};
